import * as React from 'react';

import {Button} from '../../button/button';
import {CheckboxForGroup} from '../../checkbox/checkbox-for-group';
import {CheckboxGroup} from '../../checkbox/checkbox-group';
import {FormField} from '../../form/form-field';
import {TextField} from '../../textfield/textfield';
import {Body} from '../../typography/body';

import styles from './integrations-form.module.css';

type Props = {
    appName: string;
    integrationId: string;
    checkboxValues: string[];
    submitIntegrationsForm: (
        integrationId: string,
        email: string,
        username: string,
        password: string,
        requestedActions: string[]
    ) => void;
};

/**
 *
 * Function intended to check if all form fields are filled out
 * If not, we set error messages to display to the user for each field
 * @returns an array of key-value pairs of errors and messages
 */
// @ts-expect-error
function getErrors(email, username, password, selectedValues) {
    const errs = [];

    if (email === '') {
        errs.push({key: 'email', value: 'Email is required'});
    }
    if (username === '') {
        errs.push({key: 'username', value: 'Username is required'});
    }
    if (password === '') {
        errs.push({key: 'password', value: 'Password is required'});
    }
    if (selectedValues.length <= 0) {
        errs.push({key: 'checkbox', value: 'At least one checkbox is required'});
    }

    return errs;
}

/**
This component is intended to be used to get user information for an integration request
*/
export function IntegrationsForm(props: Props) {
    const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
    const [username, setUsername] = React.useState<string>('');
    const [password, setPassword] = React.useState<string>('');
    const [email, setEmail] = React.useState<string>('');
    const [errors, setErrors] = React.useState([]);
    const [hasTriedSubmit, setHasTriedSubmit] = React.useState(false);

    React.useEffect(() => {
        // Verify we have tried to submit
        // Don't bother checking for any errors before the user has tried to submit
        if (hasTriedSubmit) {
            // @ts-expect-error
            setErrors(getErrors(email, username, password, selectedValues));
        }
    }, [username, password, email, selectedValues, hasTriedSubmit]);

    // @ts-expect-error
    const getErrorMessage = (key) => {
        // @ts-expect-error
        const err = errors.find((error) => error.key === key);
        if (err) {
            // @ts-expect-error
            return err.value;
        }

        return null;
    };

    const handleSubmit = () => {
        const currentErrors = getErrors(email, username, password, selectedValues);
        // Set if we have tried to submit the form
        // We need to track this so we know only to display error messages after submission
        // Otherwise they show up as soon as the user starts typing
        if (!hasTriedSubmit) {
            setHasTriedSubmit(true);
        }

        // Only submit if no errors
        if (currentErrors.length === 0) {
            props.submitIntegrationsForm(
                props.integrationId,
                email,
                username,
                password,
                selectedValues
            );
        } else {
            // @ts-expect-error
            setErrors(currentErrors);
        }
    };

    return (
        <div>
            <div className={styles['integration-form']}>
                <div className={styles['text-field']}>
                    <FormField
                        innerId='email'
                        label={`Your contact email`}
                        isRequired={true}
                        // @ts-expect-error
                        hasError={getErrorMessage('email')}
                        errorMessage={getErrorMessage('email')}
                    >
                        <TextField
                            id='email'
                            // @ts-expect-error
                            name='email'
                            size='normal'
                            placeholder='Email address'
                            type='email'
                            onChange={(val) => {
                                setEmail(val);
                            }}
                            value={email}
                        />
                    </FormField>
                    <FormField
                        innerId='username'
                        label={`${props.appName} username`}
                        isRequired={true}
                        // If there is no corresponding error message in the array, don't show an error
                        // @ts-expect-error
                        hasError={getErrorMessage('username')}
                        errorMessage={getErrorMessage('username')}
                    >
                        <TextField
                            id='username'
                            // @ts-expect-error
                            name='username'
                            size='normal'
                            placeholder='Username'
                            type='username'
                            onChange={(val) => {
                                setUsername(val);
                            }}
                            value={username}
                        />
                    </FormField>
                    <FormField
                        innerId='password'
                        label={`${props.appName} password`}
                        isRequired={true}
                        // @ts-expect-error
                        hasError={getErrorMessage('password')}
                        errorMessage={getErrorMessage('password')}
                    >
                        <TextField
                            id='password'
                            // @ts-expect-error
                            name='password'
                            size='normal'
                            placeholder='Password'
                            type='password'
                            onChange={(val) => {
                                setPassword(val);
                            }}
                            value={password}
                        />
                    </FormField>
                </div>
                <div className='my-32'>
                    <Body size='normal' isBold={true}>
                        What do you want your integration to do?
                    </Body>
                    <CheckboxGroup
                        onChange={(newValue: string[]) => {
                            setSelectedValues([...newValue]);
                        }}
                        name='checkbox-story'
                        selectedValues={selectedValues}
                    >
                        {props.checkboxValues.map((value) => {
                            return (
                                <div key={value} className='my-16'>
                                    <CheckboxForGroup key={value} value={value}>
                                        {value}
                                    </CheckboxForGroup>
                                </div>
                            );
                        })}
                    </CheckboxGroup>
                    <div>
                        <Body size='normal' color='error'>
                            {getErrorMessage('checkbox')}
                        </Body>
                    </div>
                </div>
                <div className='mt-32'>
                    <Button type='submit' size='big' variant='primary' onClick={handleSubmit}>
                        Start my integration
                    </Button>
                </div>
            </div>
        </div>
    );
}
